import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  computed,
  effect,
  inject,
  Input,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Subscription, take } from 'rxjs';
import { ViewportOverlayDirective } from '../../../../common/directives/viewport-overlay/viewport-overlay.directive';
import { FormatService } from '../../../../common/format/format.service';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { ApplicationStatusEnum } from '../../../../common/general-types/briefs.dto';
import { CreatorResponseDto } from '../../../../common/general-types/creators.dto';
import { PaymentMethodTypeEnum } from '../../../../common/general-types/shout-plus.dto';
import { environment } from '../../../../../environments/environment';
import { LdgImgComponent } from '../../../../atoms/ldg-img/ldg-img.component';
import { LoadingInlineComponent } from '../../../../atoms/loading-inline/loading-inline.component';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { ImageService } from '../../../../services/image/image.service';
import { BriefApplicationService } from '../../../../services/brief-application/brief-application.service';
import { BriefService } from '../../../../services/brief/brief.service';
import { CentralManagementService } from '../../../../services/central-management/central-management.service';
import { CollaboratorService } from '../../../../services/collaborator/collaborator.service';
import { CreatorStatsService } from '../../../../services/creator-stats/creator-stats.service';
import { PricingModelV2Service } from '../../../../services/pricing-model-v2/pricing-model-v2.service';
import { ReviewsFancyService } from '../../../../services/reviews-fancy/reviews-fancy.service';
import { UltraSocialService, UltraSocialCreator, UltraSocialSocialAccount } from '../../../../services/ultra-social/ultra-social';
import { RemunerationTypeIconComponent } from '../../../../shout-plus/atoms/remuneration-type-icon/remuneration-type-icon.component';
import { ShoutPlusBriefService } from '../../../../shout-plus/services/shout-plus-brief/shout-plus-brief.service';
import {
  getResolvedPaymentMethodType,
  getShoutPlusCashPrice,
  isShoutPlusBrief,
} from '../../../../shout-plus/utils/shout-plus-hire.util';
import { AmbalaGroupsComponent } from '../../../creators-v2/molecules/ambala-groups/ambala-groups.component';
import { MgcDeliveryAddressComponent } from '../delivery-address/delivery-address.component';
import { MagicSearchService } from '../../service/magic-search/magic-search.service';
import { MgcInstagramComponent } from './mgc-instagram/mgc-instagram.component';
import { MgcPortfolioComponent } from './mgc-portfolio/mgc-portfolio.component';
import { MgcSnapchat } from './mgc-snapchat/mgc-snapchat';
import { MgcTiktokComponent } from './mgc-tiktok/mgc-tiktok.component';
import { MgcYoutube } from './mgc-youtube/mgc-youtube';
import {
  CreatorProfileReview,
  ProfilePlatform,
  ProfilePrimaryAction,
  advanceReviewPage,
  appendReviewPage,
  averageEngagement,
  barWidth,
  brandMonogram,
  buildConnectedAccounts,
  failReviewPage,
  formatScore,
  initialReviewListState,
  isCreatorResponseCurrent,
  recentBrandsFitCount,
  resolvePrimaryAction,
  socialPanelKey,
  reviewHeadline,
  reviewOverall,
  reviewSubScores,
  rehirePercent,
  starGlyph,
  startReviewRequest,
  totalReach,
} from './creator-profile.helpers';
import { HasRoleDirectiveDirective } from '@/app/common/directives/has-role/has-role-directive.directive';

export type ProfileTab = 'portfolio' | 'socials' | 'reviews';

interface ReviewPageResponse {
  data?: CreatorProfileReview[];
  count?: number;
}

@Component({
  selector: 'cc-creator-profile',
  imports: [
    LdgImgComponent,
    LoadingInlineComponent,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    InfiniteScrollModule,
    MgcYoutube,
    MgcPortfolioComponent,
    MgcTiktokComponent,
    MgcInstagramComponent,
    MgcDeliveryAddressComponent,
    AmbalaGroupsComponent,
    RemunerationTypeIconComponent,
    MgcSnapchat,
    HasRoleDirectiveDirective
],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './creator-profile.component.html',
  styleUrl: './creator-profile.component.css',
  host: {
    '(document:keydown.escape)': 'closeCreatorProfile()',
    '(document:keydown.arrowleft)': 'showPreviousCreator()',
    '(document:keydown.arrowright)': 'showNextCreator()',
  },
})
export class CreatorProfileComponent implements OnInit, OnChanges, OnDestroy {
  @Input() creator!: CreatorResponseDto;
  @Input() showDeliveryAddress = false;
  @Input() showFooterActions = true;
  @Output() closeModal = new EventEmitter<void>();

  throttle = 100;
  scrollDistance = 2;
  scrollUpDistance = 2;

  readonly creatorState = signal<CreatorResponseDto | undefined>(undefined);
  readonly footerActions = signal(true);
  readonly deliveryAddressVisible = signal(false);
  readonly showAddToGroup = signal(false);
  readonly selectedTab = signal<ProfileTab>('portfolio');
  readonly selectedTab_Socials = signal<ProfilePlatform>('tiktok');
  readonly portfolioCount = signal<number | undefined>(undefined);
  readonly reviewList = signal(initialReviewListState());
  readonly recentBrands = signal<{ total: number; uniqueBrandLogos: string[] } | undefined>(undefined);
  readonly recentBrandsExpanded = signal(false);
  /** How many logos fit on one collapsed row; measured from the row width. */
  readonly recentBrandsRowCapacity = signal(8);
  private readonly recentBrandsRow = viewChild<ElementRef<HTMLElement>>('recentBrandsRow');
  readonly us_creator_id = signal<string | undefined>(undefined);
  readonly us_tiktok = signal<UltraSocialSocialAccount | undefined>(undefined);
  readonly us_instagram = signal<UltraSocialSocialAccount | undefined>(undefined);
  readonly us_youtube = signal<UltraSocialSocialAccount | undefined>(undefined);
  readonly us_snapchat = signal<UltraSocialSocialAccount | undefined>(undefined);
  readonly ultraSocialStatus = signal<'loading' | 'ready'>('loading');
  readonly socialContentKey = computed(() => socialPanelKey(this.creatorState()?.blazeFirebaseUid, this.us_creator_id()));

  readonly tabs: ProfileTab[] = ['portfolio', 'socials', 'reviews'];
  readonly social_network_tabs: ProfilePlatform[] = ['tiktok', 'instagram', 'youtube', 'snapchat'];

  public imageService = inject(ImageService);
  public formatService = inject(FormatService);
  public reviewsFancyService = inject(ReviewsFancyService);
  public briefApplicationService = inject(BriefApplicationService);
  public magicSearchService = inject(MagicSearchService);
  public collaboratorsService = inject(CollaboratorService);
  protected readonly currentUserService = inject(CurrentUserService);
  private creatorStatsService = inject(CreatorStatsService);
  private ultraSocialService = inject(UltraSocialService);
  private router = inject(Router);
  private briefService = inject(BriefService);
  private centralManagementService = inject(CentralManagementService);
  private pricingModelService = inject(PricingModelV2Service);
  private shoutPlusBriefService = inject(ShoutPlusBriefService);
  private toastNotificationService = inject(ToastNotificationService);

  private loadSubscription = new Subscription();

  protected readonly PaymentMethodTypeEnum = PaymentMethodTypeEnum;
  protected readonly brandMonogram = brandMonogram;
  protected readonly formatScore = formatScore;
  protected readonly barWidth = barWidth;
  protected readonly reviewOverall = reviewOverall;

  readonly isAdmin = computed(() => this.currentUserService.hasAnyRole([4, 6]));

  readonly currentNavIndex = computed(() => {
    const currentId = this.magicSearchService.portfolioCreatorId();
    const items = this.magicSearchService.portfolioNavItems();
    if (!currentId || !items.length) {
      return -1;
    }
    return items.findIndex((item) => item.creator.blazeFirebaseUid === currentId);
  });

  readonly canShowPrevious = computed(() => this.currentNavIndex() > 0);
  readonly canShowNext = computed(() => {
    const index = this.currentNavIndex();
    const total = this.magicSearchService.portfolioNavItems().length;
    return index >= 0 && index < total - 1;
  });
  readonly canNavigateProfiles = computed(() => this.magicSearchService.portfolioNavItems().length > 1);

  readonly isManageBriefHireContext = computed(() => {
    const application = this.magicSearchService.portfolioBriefApplication();
    return !!application && this.router.url.includes('/manage-brief');
  });

  readonly canHireFromProfile = computed(() => {
    const application = this.magicSearchService.portfolioBriefApplication();
    if (!application) {
      return false;
    }
    return (
      application.applicationStatus === ApplicationStatusEnum.Applied ||
      application.applicationStatus === ApplicationStatusEnum.HiredRemoved
    );
  });

  readonly hireCurrencyCode = computed(() => {
    const brief = this.briefService.focussedBriefSignal();
    return brief?.currentPrice?.currencyCode?.toUpperCase() ?? 'USD';
  });

  readonly hirePrice = computed(() => {
    const brief = this.briefService.focussedBriefSignal();
    const applicant = this.magicSearchService.portfolioBriefApplication();
    if (!brief || !applicant?.creator) {
      return 0;
    }

    if (isShoutPlusBrief(brief)) {
      const paymentMethod =
        this.shoutPlusBriefService.briefPaymentMethodSignal() ??
        brief.brief_payment_method ??
        brief.briefPaymentMethod;
      if (getResolvedPaymentMethodType(brief, paymentMethod) === PaymentMethodTypeEnum.CASH) {
        return getShoutPlusCashPrice(paymentMethod);
      }
      return 0;
    }

    if (!brief.currentPrice) {
      return 0;
    }

    let finalPrice = this.pricingModelService.getTotalPrice(brief, applicant.creator);
    const noVatAgenciesIds: string[] = [environment.shoutAgencyId];
    if (
      brief.currentPrice.currencyCode.toUpperCase() === 'GBP' &&
      (!brief.businessAccount?.agencyId || !noVatAgenciesIds.includes(brief.businessAccount.agencyId))
    ) {
      finalPrice = finalPrice * 1.2;
    }
    return finalPrice;
  });

  readonly hirePaymentMethodType = computed(() => {
    const brief = this.briefService.focussedBriefSignal();
    if (!isShoutPlusBrief(brief)) {
      return null;
    }
    const paymentMethod =
      this.shoutPlusBriefService.briefPaymentMethodSignal() ??
      brief?.brief_payment_method ??
      brief?.briefPaymentMethod;
    return getResolvedPaymentMethodType(brief, paymentMethod);
  });

  readonly reachLabel = computed(() => {
    const creator = this.creatorState();
    if (!creator) {
      return '—';
    }
    return this.formatService.convertIntToString(totalReach(creator));
  });

  readonly engagementLabel = computed(() => {
    const creator = this.creatorState();
    if (!creator) {
      return '—';
    }
    const value = averageEngagement(creator);
    return value == null ? '—' : `${value.toFixed(1)}%`;
  });

  readonly ratingSummary = computed(() => reviewHeadline(this.creatorState(), this.reviewList().count));

  readonly ratingLabel = computed(() => {
    const rating = this.ratingSummary().rating;
    return rating == null ? '—' : `${rating.toFixed(1)}★`;
  });

  readonly reviewStars = computed(() => starGlyph(this.ratingSummary().rating));

  readonly reviewOverview = computed(() => {
    const items = this.reviewList().items;
    const subs = reviewSubScores(items);
    const rehire = rehirePercent(items);
    const rows = subs
      ? [
          { label: 'Communication', shortLabel: 'Comms', score: subs.communication },
          { label: 'Professionalism', shortLabel: 'Prof.', score: subs.professionalism },
          { label: 'Timeliness', shortLabel: 'Time', score: subs.timeliness },
          { label: 'Quality', shortLabel: 'Quality', score: subs.quality },
        ]
      : [];
    return {
      rows,
      rehireLabel: rehire == null ? '—' : `${Math.round(rehire)}%`,
    };
  });

  readonly hasMoreReviews = computed(() => {
    const list = this.reviewList();
    return list.count != null && list.items.length < list.count;
  });

  readonly recentBrandLogos = computed(() => this.recentBrands()?.uniqueBrandLogos ?? []);

  readonly collapsedRecentBrandCount = computed(() => {
    const total = this.recentBrandLogos().length;
    return Math.min(total, this.recentBrandsRowCapacity());
  });

  readonly visibleRecentBrandLogos = computed(() => {
    const logos = this.recentBrandLogos();
    if (this.recentBrandsExpanded()) {
      return logos;
    }
    return logos.slice(0, this.collapsedRecentBrandCount());
  });

  readonly hiddenRecentBrandCount = computed(() => {
    const total = this.recentBrandLogos().length;
    if (this.recentBrandsExpanded()) {
      return 0;
    }
    return Math.max(0, total - this.collapsedRecentBrandCount());
  });

  readonly canCollapseRecentBrands = computed(
    () => this.recentBrandsExpanded() && this.recentBrandLogos().length > this.recentBrandsRowCapacity(),
  );

  readonly accountRows = computed(() => {
    const creator = this.creatorState();
    if (!creator) {
      return [];
    }
    return buildConnectedAccounts({
      creator,
      tiktok: this.us_tiktok(),
      instagram: this.us_instagram(),
      youtube: this.us_youtube(),
      snapchat: this.us_snapchat(),
    }).map((account) => ({
      ...account,
      followersLabel: account.followers == null ? '—' : this.formatService.convertIntToString(account.followers),
      engagementLabel: account.engagement == null ? null : `${account.engagement.toFixed(1)}% ENG`,
    }));
  });

  readonly connectedAccountRows = computed(() =>
    this.accountRows().filter((account) => account.connected),
  );

  readonly primaryAction = computed((): ProfilePrimaryAction => resolvePrimaryAction({
    showFooterActions: this.footerActions(),
    isManageBriefHireContext: this.isManageBriefHireContext(),
    canHireFromProfile: this.canHireFromProfile(),
    isSelected: !!this.creatorState() && this.collaboratorsService.selectedCreators().includes(this.creatorState()!),
  }));

  readonly displayName = computed(() => {
    const creator = this.creatorState();
    if (!creator) {
      return '';
    }
    const initial = creator.lastName?.trim().charAt(0);
    return initial ? `${creator.firstName} ${initial}.` : creator.firstName;
  });

  constructor() {
    effect((onCleanup) => {
      const row = this.recentBrandsRow()?.nativeElement;
      const totalLogos = this.recentBrandLogos().length;
      if (!row || totalLogos === 0) {
        return;
      }
      const measure = (): void => {
        this.recentBrandsRowCapacity.set(recentBrandsFitCount(row.clientWidth, totalLogos));
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(row);
      onCleanup(() => observer.disconnect());
    });
  }

  ngOnInit(): void {
    this.syncInputs();
    this.loadProfileData();
    this.applyPortfolioSocialTab();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.syncInputs();
    if (changes['creator'] && !changes['creator'].firstChange && this.creator) {
      this.loadProfileData();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription.unsubscribe();
  }

  showPreviousCreator(): void {
    if (this.showAddToGroup() || !this.canShowPrevious()) {
      return;
    }
    this.navigatePortfolio(-1);
  }

  showNextCreator(): void {
    if (this.showAddToGroup() || !this.canShowNext()) {
      return;
    }
    this.navigatePortfolio(1);
  }

  openAddToGroup(): void {
    this.showAddToGroup.set(true);
  }

  get publicMediaKitUrl(): string | undefined {
    const handle = this.creator?.shoutHandle?.trim();
    if (!handle) {
      return undefined;
    }
    return `${environment.mediaKit.url}/${encodeURIComponent(handle)}`;
  }

  closeAddToGroup(): void {
    this.showAddToGroup.set(false);
  }

  setSelectedTab(tab: ProfileTab): void {
    this.selectedTab.set(tab);
  }

  setSelectedTab_Socials(tab: ProfilePlatform): void {
    this.selectedTab_Socials.set(tab);
  }

  onSocialTabChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const tab = this.social_network_tabs.find((item) => item === value);
    if (tab) {
      this.setSelectedTab_Socials(tab);
    }
  }

  tabClass(tab: ProfileTab): string {
    const state = this.selectedTab() === tab
      ? 'border-primary text-primary'
      : 'border-transparent text-secondary hover:text-primary';
    return `inline-flex shrink-0 items-center gap-2 border-b-2 py-3 text-lg font-bold ${state}`;
  }

  countPillClass(tab: ProfileTab): string {
    const state = this.selectedTab() === tab
      ? 'bg-primary text-primary-foreground'
      : 'bg-muted text-secondary';
    return `rounded-full px-1.5 py-0.5 text-xs font-bold ${state}`;
  }

  tabCount(tab: ProfileTab): number | null {
    if (tab === 'portfolio') {
      return this.portfolioCount() ?? null;
    }
    if (tab === 'reviews') {
      return this.ratingSummary().count;
    }
    return null;
  }

  secondaryActionClass(layout: string): string {
    const base = 'btn-shout btn-shout-sm btn-shout-secondary shrink-0 justify-center';
    return layout === 'bar' ? `${base} size-12 px-0` : `${base} flex-1`;
  }

  tabLabel(tab: ProfileTab): string {
    switch (tab) {
      case 'portfolio':
        return 'Portfolio';
      case 'socials':
        return 'Socials';
      case 'reviews':
        return 'Reviews';
    }
  }

  onPortfolioCount(count: number): void {
    this.portfolioCount.set(count);
  }

  openAccountTab(platform: ProfilePlatform): void {
    this.selectedTab.set('socials');
    this.selectedTab_Socials.set(platform);
  }

  toggleRecentBrands(): void {
    this.recentBrandsExpanded.update((open) => !open);
  }

  copyCreatorId(): void {
    const creatorId = this.creatorState()?.blazeFirebaseUid;
    if (!creatorId) {
      return;
    }
    void navigator.clipboard.writeText(creatorId);
    void this.toastNotificationService.alert({
      title: 'Creator ID copied',
      message: 'Creator ID copied',
      type: 'success',
      icon: 'success',
    });
  }

  openAdminUserProfile(): void {
    if (this.creator?.blazeFirebaseUid) {
      void this.router.navigate(
        ['/admin-options/user-profile', this.creator.blazeFirebaseUid],
        { state: { creatorsFilterReturnUrl: this.router.url } },
      );
    }
  }

  openMissionControlModeration(): void {
    const firebaseId = this.creator?.blazeFirebaseUid?.trim();
    if (!firebaseId) {
      return;
    }
    void navigator.clipboard.writeText(firebaseId);
    window.open('https://missioncontrol.shoutugc.com/moderation', '_blank', 'noopener,noreferrer');
  }

  onScrollDownReviews(): void {
    if (this.selectedTab() !== 'reviews') {
      return;
    }
    const next = advanceReviewPage(this.reviewList());
    if (!next) {
      return;
    }
    this.reviewList.set(next);
    this.getReviews();
  }

  retryReviews(): void {
    if (!this.reviewList().error || this.reviewList().loading) {
      return;
    }
    this.getReviews();
  }

  closeCreatorProfile(): void {
    if (this.showAddToGroup()) {
      this.closeAddToGroup();
      return;
    }

    this.magicSearchService.clearCreatorPortfolio();
    this.closeModal.emit();

    if (this.centralManagementService.reopenItemisedHireAfterProfileClose()) {
      this.centralManagementService.reopenItemisedHireAfterProfileClose.set(false);
      if (this.centralManagementService.itemisedHireDataSginal()) {
        this.centralManagementService.isItemisedHireOpen.set(true);
      }
    }
  }

  openHireDrawer(): void {
    const applicant = this.magicSearchService.portfolioBriefApplication();
    const brief = this.briefService.focussedBriefSignal();
    if (!applicant || !brief) {
      return;
    }

    this.centralManagementService.reopenItemisedHireAfterProfileClose.set(false);
    this.closeCreatorProfile();
    this.centralManagementService.itemisedHireDataSginal.set({
      briefApplication: applicant,
      brief,
      creator: applicant.creator,
    });
    this.centralManagementService.isItemisedHireOpen.set(true);
  }

  private syncInputs(): void {
    this.creatorState.set(this.creator);
    this.footerActions.set(this.showFooterActions);
    this.deliveryAddressVisible.set(this.showDeliveryAddress);
  }

  private loadProfileData(): void {
    this.loadSubscription.unsubscribe();
    this.loadSubscription = new Subscription();
    this.reviewList.set(initialReviewListState());
    this.recentBrands.set(undefined);
    this.recentBrandsExpanded.set(false);
    this.us_creator_id.set(undefined);
    this.us_tiktok.set(undefined);
    this.us_instagram.set(undefined);
    this.us_youtube.set(undefined);
    this.us_snapchat.set(undefined);
    this.ultraSocialStatus.set('loading');
    this.showAddToGroup.set(false);
    this.selectedTab.set('portfolio');
    this.selectedTab_Socials.set('tiktok');
    this.portfolioCount.set(undefined);
    this.creatorState.set(this.creator);
    this.getReviews();
    this.getRecentBrands();
    this.trackProfileView();
    this.getUltraSocialCreator();
    this.applyPortfolioSocialTab();
  }

  private applyPortfolioSocialTab(): void {
    const socialTab = this.magicSearchService.portfolioSocialTab();
    if (!socialTab) {
      return;
    }
    this.selectedTab.set('socials');
    this.selectedTab_Socials.set(socialTab);
    this.magicSearchService.portfolioSocialTab.set(undefined);
  }

  private navigatePortfolio(direction: -1 | 1): void {
    if (!this.magicSearchService.showAdjacentPortfolio(direction)) {
      return;
    }
    const nextCreator = this.magicSearchService.portfolioCreator();
    if (!nextCreator) {
      return;
    }
    this.creator = nextCreator;
    this.syncHireDrawerDataAfterNavigate();
    this.loadProfileData();
  }

  private syncHireDrawerDataAfterNavigate(): void {
    if (!this.centralManagementService.reopenItemisedHireAfterProfileClose()) {
      return;
    }
    const application = this.magicSearchService.portfolioBriefApplication();
    const brief = this.briefService.focussedBriefSignal();
    if (!application || !brief) {
      return;
    }
    this.centralManagementService.itemisedHireDataSginal.set({
      briefApplication: application,
      brief,
      creator: application.creator,
    });
  }

  private trackProfileView(): void {
    const creatorId = this.creator?.blazeFirebaseUid;
    if (!creatorId) {
      return;
    }

    this.loadSubscription.add(
      this.creatorStatsService
        .trackProfileView(creatorId, this.currentUserService.focussedBrand?.id)
        .pipe(take(1))
        .subscribe({
          error: (error) => console.error('Failed to track profile view:', error),
        }),
    );
  }

  private getReviews(): void {
    const creatorId = this.creator?.blazeFirebaseUid;
    if (!creatorId || this.reviewList().loading) {
      return;
    }
    const page = this.reviewList().page;
    this.reviewList.update(startReviewRequest);
    this.loadSubscription.add(
      this.reviewsFancyService.getReviews(creatorId, 5, page * 5).pipe(take(1)).subscribe({
        next: (reviews) => {
          if (!isCreatorResponseCurrent(creatorId, this.creator?.blazeFirebaseUid)) {
            return;
          }
          const page = reviews as ReviewPageResponse;
          this.reviewList.update((state) => appendReviewPage(state, page.data ?? [], page.count ?? state.items.length));
        },
        error: () => {
          if (!isCreatorResponseCurrent(creatorId, this.creator?.blazeFirebaseUid)) {
            return;
          }
          this.reviewList.update(failReviewPage);
        },
      }),
    );
  }

  private getRecentBrands(): void {
    if (!this.creator) {
      this.recentBrands.set(undefined);
      return;
    }
    this.loadSubscription.add(
      this.briefApplicationService.getCreatorRecentBrands(this.creator.blazeFirebaseUid).pipe(take(1)).subscribe({
        next: (recentBrands) => {
          const brands = recentBrands as { total?: number; uniqueBrandLogos?: string[] };
          this.recentBrands.set({
            total: brands?.total ?? 0,
            uniqueBrandLogos: brands?.uniqueBrandLogos ?? [],
          });
        },
        error: (err: unknown) => {
          console.error('Error getting recent brands: ', err);
        },
      }),
    );
  }

  private getUltraSocialCreator(): void {
    const creatorId = this.creator?.blazeFirebaseUid;
    if (!creatorId) {
      this.ultraSocialStatus.set('ready');
      return;
    }
    this.ultraSocialStatus.set('loading');
    this.loadSubscription.add(
      this.ultraSocialService.getUltraSocialCreator(creatorId).pipe(take(1)).subscribe({
        next: (ultraSocialCreator: UltraSocialCreator) => {
          if (!isCreatorResponseCurrent(creatorId, this.creator?.blazeFirebaseUid)) {
            return;
          }
          this.us_creator_id.set(ultraSocialCreator.id);
          this.us_tiktok.set(ultraSocialCreator.socialAccounts.find((account) => account.platform === 'tiktok'));
          this.us_instagram.set(ultraSocialCreator.socialAccounts.find((account) => account.platform === 'instagram'));
          this.us_youtube.set(ultraSocialCreator.socialAccounts.find((account) => account.platform === 'youtube'));
          this.us_snapchat.set(ultraSocialCreator.socialAccounts.find((account) => account.platform === 'snapchat'));
          this.ultraSocialStatus.set('ready');
        },
        error: (err: unknown) => {
          console.error('Error getting ultraSocialCreator: ', err);
          if (!isCreatorResponseCurrent(creatorId, this.creator?.blazeFirebaseUid)) {
            return;
          }
          this.ultraSocialStatus.set('ready');
        },
      }),
    );
  }


  getTabClass(tab: 'portfolio' | 'socials' | 'reviews') {
    return this.selectedTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  getTabClass_Socials(tab: ProfilePlatform) {
    const index = this.social_network_tabs.indexOf(tab);
    const edge = index === 0
      ? 'rounded-l-lg'
      : index === this.social_network_tabs.length - 1
        ? 'rounded-r-lg'
        : '';
    const state = this.selectedTab_Socials() === tab
      ? 'text-gray-900 dark:text-white'
      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white';
    return `group relative min-w-0 shrink-0 overflow-hidden whitespace-nowrap px-3 py-2 text-center text-sm font-medium hover:bg-gray-50 focus:z-10 dark:hover:bg-white/5 cursor-pointer ${edge} ${state}`;
  }

  getSocialTabIndicatorClass(tab: ProfilePlatform) {
    return this.selectedTab_Socials() === tab
      ? 'absolute inset-x-0 bottom-0 h-0.5 bg-indigo-500 dark:bg-indigo-400'
      : 'absolute inset-x-0 bottom-0 h-0.5 bg-transparent';
  }

  getTabLabel(tab: 'portfolio' | 'socials' | 'reviews') {
    switch (tab) {
      case 'portfolio':
        return 'Portfolio';
      case 'socials':
        return 'Socials';
      case 'reviews':
        return 'Reviews';
    }
  }

  getTabLabel_Socials(tab: ProfilePlatform) {
    switch (tab) {
      case 'tiktok':
        return 'TikTok';
      case 'instagram':
        return 'Instagram';
      case 'youtube':
        return 'YouTube';
      case 'snapchat':
        return 'Snapchat';
    }
  }
}
